import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Clock, MapPin } from 'lucide-react';

export const UrgentCarePortfolio: React.FC = () => {
  const technologies = [
    'WordPress', 'PHP', 'MySQL', 'AJAX', 'Google Maps API', 'SMS Integration'
  ];

  const features = [
    'Real-time Wait Times',
    'Online Check-in System',
    'Location Finder',
    'Insurance Verification',
    'Symptom Checker',
    'Urgent Care vs ER Guide',
    'Mobile App Integration',
    'Multi-location Management'
  ];

  const results = [
    { metric: 'Wait Time Reduction', value: '35%', description: 'Average patient wait time decreased' },
    { metric: 'Online Check-ins', value: '70%', description: 'Of patients now check-in online' },
    { metric: 'Patient Satisfaction', value: '94%', description: 'Positive feedback on new system' },
    { metric: 'Operational Efficiency', value: '50%', description: 'Improvement in patient flow' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Urgent Care
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                QuickCare Urgent Centers
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A multi-location urgent care platform featuring real-time wait times, online check-in, 
                and location-based services that improved patient flow and reduced wait times by 35%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Urgent Care Center"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  QuickCare Urgent Centers operates 12 locations across the metropolitan area and needed 
                  a unified digital platform to manage patient flow, reduce wait times, and improve the 
                  overall patient experience across all locations.
                </p>
                <p className="mb-6">
                  We developed a comprehensive WordPress solution with custom PHP development for real-time 
                  wait time tracking, online check-in capabilities, and location-based services. The platform 
                  integrates with their existing patient management systems and provides real-time updates 
                  across all locations.
                </p>
                <p>
                  The implementation resulted in a 35% reduction in wait times, 70% of patients using online 
                  check-in, and a 50% improvement in operational efficiency.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">10 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">January 2024</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Locations:</span>
                    <span className="font-medium">12 centers</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced features designed to streamline urgent care operations and enhance patient experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dramatic improvements in patient satisfaction and operational efficiency across all locations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Clock className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Real-time Data Sync</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Synchronizing real-time wait times and patient flow data across 12 locations while 
                maintaining accuracy and system performance.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented AJAX-based real-time updates with optimized 
                database queries and caching strategies to ensure accurate, up-to-the-minute information.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <MapPin className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Multi-location Management</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Creating a unified system that could handle different workflows and requirements 
                across multiple urgent care locations.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed a flexible, modular architecture with location-specific 
                configurations while maintaining centralized management and reporting capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The new platform has transformed our operations. Patients can see wait times before they arrive, 
            check-in online, and we've reduced our average wait time by 35%. It's been a game-changer."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Dr. Michael Chen"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Dr. Michael Chen</div>
              <div className="text-primary-200">Medical Director, QuickCare Urgent Centers</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Streamline Your Healthcare Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a solution that improves patient flow and satisfaction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};